/**
 * Floating "Copy" affordance anchored to the current text selection.
 *
 * There is no real trigger element to anchor Radix's Tooltip to -- the
 * selection can start and end anywhere in the document -- so an invisible,
 * zero-size span is positioned at the selection's own viewport coordinates
 * (from `editor.view.coordsAtPos`, not `window.getSelection()`: the latter's
 * shadow-root behavior is inconsistent across browsers, per the comment on
 * `readSelection` in DocumentWorkspaceElement, whereas ProseMirror's own
 * coordinates are unaffected by the shadow boundary) and used as the
 * Tooltip's trigger, with `open` fully controlled instead of hover-driven.
 */
import React from 'react';

import { type Editor } from '@tiptap/react';
import { Check, Copy } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export interface SelectionAnchor {
  /** Viewport-relative, matching `getBoundingClientRect()`'s coordinate space. */
  top: number;
  left: number;
  right: number;
}

interface SelectionCopyTooltipProps {
  /** `null` hides the tooltip -- there is nothing selected to copy. */
  anchor: SelectionAnchor | null;
  text: string;
  /** Identifies the selection; forces a remount on a new one -- see `key` below. */
  selectionFrom: number | null;
  selectionTo: number | null;
  /** Used to resolve the tooltip's portal target -- see portalContainer below. */
  editor: Editor;
}

export function SelectionCopyTooltip({
  anchor,
  text,
  selectionFrom,
  selectionTo,
  editor,
}: Readonly<SelectionCopyTooltipProps>) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    setCopied(false);
  }, [selectionFrom, selectionTo]);

  const handleCopy = React.useCallback(() => {
    navigator.clipboard
      .writeText(text)
      .then(() => setCopied(true))
      .catch(() => {
        // Clipboard write denied/unsupported (e.g. no user-activation, insecure
        // context) -- nothing to confirm, leave the copy icon as-is.
      });
  }, [text]);

  if (!anchor) return null;

  // Not memoized -- editor.view.dom may not be attached yet on first mount.
  const viewerContainer = editor.view.dom.closest('.fp-document-viewer-container');
  const root = editor.view.dom.getRootNode();
  const portalContainer =
    viewerContainer ?? (root instanceof ShadowRoot ? root : document.body);

  return (
    <TooltipProvider>
      <Tooltip key={`${selectionFrom}-${selectionTo}`} open>
        <TooltipTrigger asChild>
          <span
            style={{
              position: 'fixed',
              top: anchor.top,
              left: (anchor.left + anchor.right) / 2,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="p-0 bg-indigo-900"
          arrowClassName="fill-indigo-900 group-hover:fill-indigo-900/80"
          container={portalContainer}
        >
          <button
            type="button"
            onClick={handleCopy}
            onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
            className="appearance-none select-none shadow-none cursor-pointer py-2 px-3 flex items-center gap-1 rounded-md text-sm font-normal hover:bg-indigo-900/80 border-none bg-[var(--primary-navyblue,#10147E)] text-white"
          >
            {/* katex.min.css sets a bare `svg{position:absolute;width:100%}` -- override it here. */}
            {copied ? (
              <Check size={14} className="static size-3.5 shrink-0" />
            ) : (
              <Copy size={14} className="static size-3.5 shrink-0" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
